import React from 'react';

interface GenieLogoProps {
  className?: string;
  size?: number;
}

/**
 * Genie Logo Component
 * A stylized magical lamp/genie icon for the TargetX AI assistant
 */
export const GenieLogo: React.FC<GenieLogoProps> = ({ className = '', size = 24 }) => {
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Genie Logo"
    >
      {/* Magic sparkles */}
      <g opacity="0.9">
        <circle cx="16" cy="12" r="2" fill="currentColor">
          <animate
            attributeName="opacity"
            values="1;0.3;1"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="48" cy="8" r="1.5" fill="currentColor">
          <animate
            attributeName="opacity"
            values="0.3;1;0.3"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="52" cy="20" r="2" fill="currentColor">
          <animate
            attributeName="opacity"
            values="0.5;1;0.5"
            dur="1.8s"
            repeatCount="indefinite"
          />
        </circle>
        <path
          d="M20 16l2-4 2 4-4-2 4 2z"
          fill="currentColor"
          opacity="0.8"
        >
          <animate
            attributeName="opacity"
            values="0.8;0.3;0.8"
            dur="2.5s"
            repeatCount="indefinite"
          />
        </path>
      </g>

      {/* Magical swirl/smoke - representing the genie */}
      <path
        d="M32 6 C38 6 42 10 42 16 C42 22 38 26 34 28 C36 30 38 34 38 38 C38 42 35 44 32 44 C29 44 26 42 26 38 C26 34 28 30 30 28 C26 26 22 22 22 16 C22 10 26 6 32 6"
        fill="currentColor"
        opacity="0.2"
      />
      
      {/* Genie face - stylized */}
      <ellipse cx="32" cy="22" rx="10" ry="12" fill="currentColor" opacity="0.15" />
      
      {/* Eyes */}
      <ellipse cx="28" cy="20" rx="2" ry="2.5" fill="currentColor" />
      <ellipse cx="36" cy="20" rx="2" ry="2.5" fill="currentColor" />
      
      {/* Friendly smile */}
      <path
        d="M28 26 Q32 30 36 26"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />

      {/* Magic lamp base */}
      <path
        d="M18 52 C18 48 24 44 32 44 C40 44 46 48 46 52 L46 54 C46 56 44 58 40 58 L24 58 C20 58 18 56 18 54 Z"
        fill="currentColor"
        opacity="0.9"
      />
      
      {/* Lamp spout */}
      <path
        d="M42 50 L50 46 L52 48 L46 52"
        fill="currentColor"
        opacity="0.9"
      />
      
      {/* Lamp handle */}
      <path
        d="M18 50 C14 48 12 50 14 54 C16 56 18 54 18 52"
        fill="currentColor"
        opacity="0.8"
      />
      
      {/* Lamp decoration line */}
      <path
        d="M22 54 L42 54"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.5"
      />

      {/* DNA helix integration - connecting biotech theme */}
      <g opacity="0.6">
        <path
          d="M26 36 Q32 32 38 36"
          stroke="currentColor"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M26 40 Q32 44 38 40"
          stroke="currentColor"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
        />
        <line x1="29" y1="34" x2="29" y2="42" stroke="currentColor" strokeWidth="1" opacity="0.5" />
        <line x1="35" y1="34" x2="35" y2="42" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      </g>
    </svg>
  );
};

/**
 * Alternative simplified Genie icon for smaller displays
 */
export const GenieIconSimple: React.FC<GenieLogoProps> = ({ className = '', size = 24 }) => {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Genie"
    >
      {/* Sparkle */}
      <path d="M12 2l1 3 3-1-1 3 3 1-3 1 1 3-3-1-1 3-1-3-3 1 1-3-3-1 3-1-1-3 3 1z" fill="currentColor" opacity="0.3" />
      
      {/* Lamp */}
      <path d="M8 18c-1 0-2 1-2 2h12c0-1-1-2-2-2" />
      <ellipse cx="12" cy="16" rx="5" ry="2" />
      
      {/* Smoke/Genie */}
      <path d="M12 14c-2 0-3-2-3-4s1-4 3-4 3 2 3 4-1 4-3 4" fill="currentColor" opacity="0.2" />
      
      {/* Face */}
      <circle cx="10.5" cy="9" r="0.5" fill="currentColor" />
      <circle cx="13.5" cy="9" r="0.5" fill="currentColor" />
      <path d="M10.5 11c.5.5 2.5.5 3 0" />
    </svg>
  );
};

export default GenieLogo;
